import { useEffect, useMemo, useState } from "react";

import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type {
  CasinoKpiFilterOption,
  CasinoKpiFilterValues,
} from "../../../../queries/casino-kpis/casinoKpiFilters";

import styles from "./CasinoKpiFiltersSection.module.css";

export const EMPTY_CASINO_KPI_FILTERS: CasinoKpiFilterValues = {
  money_type: [],
};

type Props = {
  value: CasinoKpiFilterValues;
  options: CasinoKpiFilterOption[];
  loading?: boolean;
  onApply: (filters: CasinoKpiFilterValues) => void;
};

const CasinoKpiFiltersSection = ({
  value,
  options,
  loading = false,
  onApply,
}: Props) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const hasChanges = useMemo(
    () =>
      JSON.stringify(draft.money_type) !== JSON.stringify(value.money_type),
    [draft.money_type, value.money_type],
  );

  return (
    <section
      className={styles.section}
      aria-label={AppStrings.PAM_REPORT_FILTERS}
      data-testid="casino-kpi-filters-section"
    >
      <div className={styles.field}>
        <span className={styles.label}>
          {AppStrings.CASINO_KPI_CURRENCY_TYPE}
        </span>
        <Select
          className={styles.filterSelect}
          mode="multiple"
          value={draft.money_type}
          options={options}
          onChange={(moneyType) =>
            setDraft({ money_type: moneyType as string[] })
          }
          aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
          placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
          maxTagCount="responsive"
          showSearch
          optionFilterProp="label"
          loading={loading}
        />
      </div>
      <div className={styles.actions}>
        <Button
          intent="neutral"
          variant="outline"
          size="large"
          onClick={() => setDraft(EMPTY_CASINO_KPI_FILTERS)}
        >
          {AppStrings.CLEAR_FILTERS}
        </Button>
        <Button
          intent="primary"
          size="large"
          onClick={() => onApply(draft)}
          disabled={!hasChanges}
        >
          {AppStrings.APPLY}
        </Button>
      </div>
    </section>
  );
};

export default CasinoKpiFiltersSection;
